import styled from '@emotion/styled';
import { Checkbox } from '@kompare/kompare-ui-library';
import React, { useCallback, useState } from 'react';
import { i18n, setGlobalLoading, useAppDispatch, useDebouncedSaveHealthQuestionnaireAnswer } from 'src/common';
import type { HealthQuestionnaireQuestionsQuery } from 'src/common/api/apollo/additional';
import { colors } from 'src/theme';
import { Button } from '../../buttons';
import { Card } from '../../cards/card';
import { Flex } from '../../grid';
import { BackLink } from '../../other/backLink.other';
import { Text } from '../../typography';
import type { AnyQuestion, QuestionnaireAnswers } from './healthQuestionnaire.types';
import {
  CONFIRMATION_ERROR_KEY,
  EMPTY_ANSWERS,
  cleanupHiddenFollowUpFields,
  collectValidationErrors,
} from './healthQuestionnaire.types';
import { QUESTION_ID_PREFIX, QuestionSection } from './questionSection.part';

export type { QuestionnaireAnswers } from './healthQuestionnaire.types';

const CONFIRMATION_DOM_ID = `${QUESTION_ID_PREFIX}${CONFIRMATION_ERROR_KEY}`;

const ConfirmationBox = styled.div<{ $hasError: boolean }>(({ $hasError }) => ({
  backgroundColor: '#F2EAF7',
  border: `1px solid ${$hasError ? colors.error1 : '#F2F2F2'}`,
  borderRadius: 8,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}));

const ErrorText = styled.span({
  fontSize: 13,
  color: colors.error1,
  fontWeight: 500,
});

export interface HealthQuestionnaireFormProps {
  questions: HealthQuestionnaireQuestionsQuery['healthQuestionnaireQuestions'];
  onConfirm?: (answers: QuestionnaireAnswers) => void;
  backPath?: string;
  onBack?: () => void;
  confirmDisabled?: boolean;
  initialValues?: Partial<QuestionnaireAnswers>;
  className?: string;
  /** When set, saves answer deltas via `saveHealthQuestionnaireAnswers` after 1s idle */
  autoSave?: { offerId: string; brandCode: string };
  admin?: boolean;
  /** Render answers as static text instead of inputs/textareas/selects (used by the print page). */
  displayMode?: boolean;
}

export const HealthQuestionnaireForm: React.FC<HealthQuestionnaireFormProps> = ({
  questions,
  onConfirm,
  backPath,
  initialValues,
  className,
  autoSave,
  confirmDisabled,
  admin,
  displayMode,
}) => {
  const [answers, setAnswers] = useState<QuestionnaireAnswers>(() => ({
    ...EMPTY_ANSWERS,
    ...initialValues,
    ...(admin ? { confirmed: true } : {}),
  }));
  const dispatch = useAppDispatch();

  const [errors, setErrors] = useState<Set<string>>(new Set());

  const { isAutoSaveBusy } = useDebouncedSaveHealthQuestionnaireAnswer({
    answers,
    offerId: autoSave?.offerId,
    brandCode: autoSave?.brandCode,
    questions: questions as AnyQuestion[],
  });

  const handleChange = useCallback((next: QuestionnaireAnswers) => {
    const cleaned = cleanupHiddenFollowUpFields(next, questions as AnyQuestion[]);
    setAnswers(cleaned);
    setErrors((prev) => (prev.size > 0 ? new Set() : prev));
  }, [questions]);

  const handleSubmit = useCallback(() => {
    dispatch(setGlobalLoading(true));
    const fieldErrors = collectValidationErrors(questions as AnyQuestion[], answers);

    if (!answers.confirmed) {
      fieldErrors.add(CONFIRMATION_ERROR_KEY);
    }

    if (fieldErrors.size > 0) {
      setErrors(fieldErrors);

      requestAnimationFrame(() => {
        const elements = Array.from(fieldErrors)
          .map((errCode) => document.getElementById(`${QUESTION_ID_PREFIX}${errCode}`))
          .filter((el): el is HTMLElement => el != null);

        const topmost = elements.reduce<HTMLElement | null>((best, el) => {
          if (!best) return el;
          return el.getBoundingClientRect().top < best.getBoundingClientRect().top ? el : best;
        }, null);

        topmost?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      dispatch(setGlobalLoading(false));
      return;
    }

    onConfirm?.(answers)

    if (onConfirm === undefined) {
      dispatch(setGlobalLoading(false));
    }
  }, [answers, questions, onConfirm]);

  const confirmationHasError = errors.has(CONFIRMATION_ERROR_KEY);

  return (
    <Card radius={12} padding={8} className={className}>
      <Flex vertical gap={24}>
        <Text type="small" color={colors.text1} bold>
          {i18n.t('pages.healthQuestionnaire.title')}
        </Text>

        {questions.map((question) => (
          <QuestionSection
            key={question.code}
            question={question}
            allQuestions={questions as AnyQuestion[]}
            answers={answers}
            onChange={handleChange}
            errors={errors}
            displayMode={displayMode}
          />
        ))}

        <ConfirmationBox $hasError={confirmationHasError} id={CONFIRMATION_DOM_ID}>
          <Text type="base" bold style={{ marginBottom: 8 }}>
            {i18n.t('pages.healthQuestionnaire.confirmationTitle')}
          </Text>
          <Checkbox
            checked={answers.confirmed || onConfirm === undefined || admin}
            onChange={() => setAnswers((prev) => ({ ...prev, confirmed: !prev.confirmed }))}
            checkboxSize="small"
            label={
              <Text type="base">
                {i18n.t('pages.healthQuestionnaire.confirmationText')}
              </Text>
            }
          />
          {confirmationHasError && <ErrorText>Morate potvrditi prije nastavka</ErrorText>}
        </ConfirmationBox>
        {onConfirm && (
          <Flex justify="space-between" align="center" gap={8}>
            {admin ? null : <BackLink text={i18n.t('pages.healthQuestionnaire.backButton')} path={backPath} />}
            {admin ? <div></div> : null}
            <Button
              btnType="primary"
              size="large"
              text={i18n.t('pages.healthQuestionnaire.confirmButton')}
              onClick={handleSubmit}
              disabled={Boolean(confirmDisabled || (autoSave && isAutoSaveBusy))}
              style={{ minWidth: 178 }}
            />
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
